import React from 'react';

const MODULE_LABELS: Record<string, string> = {
  incidents: 'Incidentes',
  requests: 'Requisições',
  problems: 'Problemas',
  changes: 'Mudanças',
  knowledge_base: 'Base de Conhecimento',
  forms: 'Formulários',
  reports: 'Relatórios',
  asset_management: 'Gestão de Ativos',
  software_management: 'Softwares e Licenças',
  sla_management: 'Gestão de SLA',
};
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, 
  Check, 
  Calendar, 
  RefreshCw, 
  ExternalLink, 
  Store, 
  Loader2,
  AlertTriangle,
  Package,
  XCircle,
  RotateCcw
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useMarketplace } from '@/hooks/useMarketplace';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import LicenseUsageCard from './LicenseUsageCard';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MyLicenseViewProps {
  onNavigateToMarketplace?: () => void;
}

const MyLicenseView = ({ onNavigateToMarketplace }: MyLicenseViewProps) => {
  const { myLicense, myModules, isLoading } = useMarketplace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoadingPortal, setIsLoadingPortal] = React.useState(false);
  const [isLoadingCancel, setIsLoadingCancel] = React.useState(false);

  const plan = myLicense?.license_plan;
  const cancelAtPeriodEnd = myLicense?.cancel_at_period_end === true;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="bg-green-600">
            <Check className="h-3 w-3 mr-1" />
            Ativo
          </Badge>
        );
      case 'trial':
        return (
          <Badge variant="secondary" className="bg-blue-100 text-blue-700">
            <Calendar className="h-3 w-3 mr-1" />
            Trial
          </Badge>
        );
      case 'suspended':
        return (
          <Badge variant="destructive">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Suspenso
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="outline">
            Cancelado
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleOpenStripePortal = async () => {
    try {
      setIsLoadingPortal(true);
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      console.error('Error opening Stripe portal:', error);
      toast({
        title: 'Erro ao abrir portal',
        description: error.message || 'Não foi possível abrir o portal do cliente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsLoadingCancel(true);
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { action: 'cancel' },
      });
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['client-license'] });
      toast({
        title: 'Assinatura cancelada',
        description: data?.cancel_at 
          ? `Sua assinatura será encerrada em ${format(new Date(data.cancel_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}.`
          : 'Sua assinatura será cancelada ao final do período atual.',
      });
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      toast({
        title: 'Erro ao cancelar',
        description: error.message || 'Não foi possível cancelar a assinatura.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCancel(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsLoadingCancel(true);
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { action: 'reactivate' },
      });
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['client-license'] });
      toast({
        title: 'Assinatura reativada',
        description: 'Sua assinatura foi reativada com sucesso.',
      });
    } catch (error: any) {
      console.error('Error reactivating subscription:', error);
      toast({
        title: 'Erro ao reativar',
        description: error.message || 'Não foi possível reativar a assinatura.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCancel(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <CreditCard className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Minha Licença</h1>
          <p className="text-muted-foreground">
            Gerencie sua assinatura e uso
          </p>
        </div>
      </div>

      {/* Cancellation Warning Banner */}
      {myLicense && cancelAtPeriodEnd && (
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Cancelamento programado
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Sua assinatura será cancelada em{' '}
              <strong>
                {myLicense.end_date
                  ? format(new Date(myLicense.end_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                  : 'data não definida'}
              </strong>
              . Após essa data, você perderá acesso aos módulos contratados.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
              onClick={handleReactivateSubscription}
              disabled={isLoadingCancel}
            >
              {isLoadingCancel ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Reativar Assinatura
            </Button>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg">Plano Atual</CardTitle>
              <CardDescription>Detalhes da sua assinatura</CardDescription>
            </div>
            {myLicense && getStatusBadge(myLicense.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {myLicense ? (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground">Plano</p>
                  <p className="text-lg font-semibold">{plan?.name || 'Não definido'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ciclo de Cobrança</p>
                  <p className="text-lg font-semibold flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    {myLicense.billing_cycle === 'yearly' ? 'Anual' : 'Mensal'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Renovação</p>
                  <p className="text-lg font-semibold flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {myLicense.end_date 
                      ? format(new Date(myLicense.end_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                      : 'Sem data definida'
                    }
                  </p>
                </div>
              </div>

              {myLicense.status === 'trial' && myLicense.trial_end_date && (
                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Período de avaliação:</strong> Seu trial expira em{' '}
                    {format(new Date(myLicense.trial_end_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={handleOpenStripePortal}
                  disabled={isLoadingPortal || !myLicense.stripe_customer_id}
                >
                  {isLoadingPortal ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  Portal do Cliente
                </Button>

                {!cancelAtPeriodEnd && myLicense.status === 'active' && myLicense.stripe_subscription_id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isLoadingCancel}>
                        {isLoadingCancel ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <XCircle className="h-4 w-4 mr-2" />
                        )}
                        Cancelar Assinatura
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancelar assinatura?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                          <p>
                            Ao cancelar, sua assinatura permanecerá ativa até o final do período de cobrança atual
                            {myLicense.end_date && (
                              <> (<strong>{format(new Date(myLicense.end_date), "dd/MM/yyyy", { locale: ptBR })}</strong>)</>
                            )}
                            .
                          </p>
                          <p>
                            Após essa data, você perderá acesso a todos os módulos contratados e funcionalidades premium.
                          </p>
                          <p className="font-medium text-foreground">
                            Você pode reativar a qualquer momento antes do encerramento.
                          </p>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Manter assinatura</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleCancelSubscription}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Confirmar cancelamento
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma licença encontrada para sua conta.</p>
              <p className="text-sm">Entre em contato com o suporte.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage Cards */}
      {myLicense && (
        <>
          <h2 className="text-lg font-semibold">Uso de Licenças</h2>
          <LicenseUsageCard license={myLicense as any} />
        </>
      )}

      {/* Active Modules */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Módulos Contratados ({myModules.length})</CardTitle>
              <CardDescription>Funcionalidades ativas na sua conta</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {myModules.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {myModules.map((module) => (
                <Badge 
                  key={module.id} 
                  variant="secondary"
                  className="text-sm py-1.5 px-3"
                >
                  <Check className="h-3 w-3 mr-1.5" />
                  {MODULE_LABELS[module.module_code] || module.module_code}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nenhum módulo ativo</p>
            </div>
          )}

          <div className="mt-4 pt-4 border-t">
            <Button 
              variant="link" 
              className="p-0 h-auto"
              onClick={onNavigateToMarketplace}
            >
              <Store className="h-4 w-4 mr-2" />
              Gerenciar no Marketplace →
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyLicenseView;
